<!-- app/components/base/FilterSelect.vue -->
<script lang="ts" setup>
interface Option {
  value: string | number;
  label: string;
}

const props = defineProps<{
  mode: 'single' | 'multi';
  modelValue: string | number | string[];
  options: Option[];
  triggerLabel: string;
  applyLabel?: string;
  dataTestId?: string;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string | number | string[]): void;
}>();

const container = ref<HTMLElement>();
const panel = ref<HTMLElement>();
const isExpanded = ref(false);
const opensUp = ref(false);

const updateDirection = () => {
  if (!container.value) return;
  const rect = container.value.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  opensUp.value = spaceBelow < 320;
};

// Pending (uncommitted) selection for multi-mode.
const pending = ref<string[]>(props.mode === 'multi' && Array.isArray(props.modelValue) ? [...props.modelValue] : []);

watch(
  () => props.modelValue,
  (value) => {
    if (props.mode === 'multi') pending.value = Array.isArray(value) ? [...(value as string[])] : [];
  },
);

const triggerText = computed(() => {
  if (props.mode === 'single') {
    const selected = props.options.find((option) => option.value === props.modelValue);
    return selected ? selected.label : props.triggerLabel;
  }
  return props.triggerLabel;
});

const close = () => {
  isExpanded.value = false;
};

const toggle = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) {
    nextTick(updateDirection);
    if (props.mode === 'multi') {
      pending.value = Array.isArray(props.modelValue) ? [...(props.modelValue as string[])] : [];
    }
  }
};

// single-mode: commit immediately and close
const selectSingle = (value: string | number) => {
  emit('update:modelValue', value);
  close();
};

// multi-mode: toggle a checkbox in the pending state
const toggleMulti = (value: string) => {
  const index = pending.value.indexOf(value);
  if (index === -1) pending.value.push(value);
  else pending.value.splice(index, 1);
};

const isChecked = (value: string) => pending.value.includes(value);

const applyMulti = () => {
  emit('update:modelValue', [...pending.value]);
  close();
};

const onGlobalClick = (event: MouseEvent) => {
  if (isExpanded.value && container.value && !container.value.contains(event.target as HTMLElement)) {
    close();
  }
};

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isExpanded.value) close();
};

onMounted(() => {
  window.addEventListener('click', onGlobalClick);
  window.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  window.removeEventListener('click', onGlobalClick);
  window.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div ref="container" class="filterSelect" :data-test-id="dataTestId">
    <button
      type="button"
      class="filterSelect__trigger"
      :class="{ filterSelect__trigger_expanded: isExpanded }"
      :data-test-id="dataTestId ? `${dataTestId}-trigger` : undefined"
      @click="toggle"
    >
      <span class="filterSelect__triggerLabel">{{ triggerText }}</span>
      <SvgoSimpleChevronThinDown
        class="filterSelect__chevron"
        :class="{ filterSelect__chevron_expanded: isExpanded }"
      />
    </button>

    <div ref="panel" v-show="isExpanded" class="filterSelect__panel" :class="{ filterSelect__panel_up: opensUp }">
      <template v-if="mode === 'single'">
        <button
          v-for="option in options"
          :key="option.value"
          type="button"
          class="filterSelect__option"
          :class="{ filterSelect__option_selected: option.value === modelValue }"
          :data-test-id="dataTestId ? `${dataTestId}-option` : undefined"
          @click="selectSingle(option.value)"
        >
          {{ option.label }}
        </button>
      </template>

      <template v-else>
        <label
          v-for="option in options"
          :key="option.value"
          class="filterSelect__checkRow"
          :data-test-id="dataTestId ? `${dataTestId}-option` : undefined"
        >
          <span
            class="filterSelect__checkbox"
            :class="{ filterSelect__checkbox_checked: isChecked(String(option.value)) }"
            aria-hidden="true"
          />
          <input
            type="checkbox"
            class="filterSelect__checkInput"
            :value="option.value"
            :checked="isChecked(String(option.value))"
            @change="toggleMulti(String(option.value))"
          />
          <span class="filterSelect__checkLabel">{{ option.label }}</span>
        </label>

        <button
          type="button"
          class="filterSelect__apply"
          :data-test-id="dataTestId ? `${dataTestId}-apply` : undefined"
          @click="applyMulti"
        >
          {{ applyLabel }}
        </button>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.filterSelect {
  $self: &;

  position: relative;
  display: inline-block;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.2px;
  color: #121212;

  &__trigger {
    display: flex;
    align-items: center;
    gap: 14px;
    box-sizing: border-box;
    width: max-content;
    height: 42px;
    background: #fff;
    border: 1px solid #c0c0c0;
    border-radius: 38px;
    padding: 0 14px;
    font-size: 16px;
    cursor: pointer;
  }

  &__triggerLabel {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__chevron {
    width: 14px;
    height: 7px;
    display: block;
    flex-shrink: 0;
    transition: transform 0.3s ease;

    // nuxt-svgo marks the icon `.nuxt-icon--fill` and forces `fill: currentcolor`
    // on every child, which would close the open chevron path into a solid
    // triangle. Keep it a thin stroked "галочка".
    path {
      fill: none;
    }

    &_expanded {
      transform: rotate(180deg);
    }
  }

  &__panel {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 2;
    background-color: #fff;
    padding: 12px 16px;
    border: 1px solid #c0c0c0;
    border-radius: 16px;
    min-width: 215px;
    width: max-content;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

    &_up {
      top: auto;
      bottom: calc(100% + 6px);
    }
  }

  &__option {
    background-color: transparent;
    outline: none;
    border: none;
    padding: 8px 12px;
    font-size: 16px;
    color: #121212;
    display: block;
    width: 100%;
    text-align: left;
    cursor: pointer;
    border-radius: 20px;
    transition: background-color 0.3s ease;

    &:not(&_selected):hover {
      background-color: rgba(18, 18, 18, 0.06);
    }

    &_selected {
      background-color: #121212;
      color: #fff;
    }
  }

  &__checkRow {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px;
    cursor: pointer;
  }

  &__checkbox {
    width: 18px;
    height: 18px;
    border: 1px solid #121212;
    border-radius: 4px;
    flex-shrink: 0;
    position: relative;
    background-color: transparent;
    transition: background-color 0.2s ease;

    &_checked {
      background-color: #121212;
    }
  }

  &__checkInput {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__checkLabel {
    color: #121212;
  }

  &__apply {
    margin-top: 8px;
    height: 42px;
    padding: 0 16px;
    background: #121212;
    color: #fff;
    border: none;
    border-radius: 30px;
    cursor: pointer;
    font-size: 16px;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.85;
    }
  }
}
</style>
